import React, { useContext, ComponentType, useCallback } from 'react';
import Typography from '@material-ui/core/Typography';
import ButtonGroup from '@material-ui/core/ButtonGroup/ButtonGroup';
import Button from '@material-ui/core/Button';
import { DetailContext } from '@eas/common-web';

import {
  MapEvidenceContext,
  MapEvidenceView,
} from '../../components/evidence/map-evidence/map-context';

import { IrzFacility } from '../../models';
import { EvidenceAPI } from '../../enums';
import { useMarkerPopupFactory } from '../../components/map/hook/marker-popup-factory';
import { fetchIrzFacility } from './irz-facilities-api';
import {
  CrzpMarker,
  MarkerType,
  MarkerProps,
} from '../../components/map/map-types';
import { MapContainer } from '../../components/map/map-container';
import { RemoteMarkers } from '../../components/map/remote-markers';
import {
  createMarkerIcon,
  createLeafletMarker,
} from '../../components/map/marker';

export function FacilityMarkerTitle({ name }: { name: string }) {
  return (
    <>
      <Typography variant="body2">
        Název provozovny: <b>{name}</b>
      </Typography>
    </>
  );
}

export function FacilityMarkerBody({
  data,
  setActive,
  Actions,
}: {
  data: IrzFacility;
  setActive: (id: string) => void;
  Actions?: ComponentType<{ data: IrzFacility }>;
}) {
  return (
    <>
      <Typography variant="body2">
        IČP: <b>{data?.facilityId}</b>
      </Typography>
      <br />
      <Typography variant="body2">
        Adresní místo: <b>{data?.address?.addressPlaceRuian?.label}</b>
      </Typography>
      <Typography component="div" variant="body2">
        <>
          <p>
            Zeměpisná šířka: <b>{data?.wgs84Coordinates?.lat}</b>
          </p>
          <p>
            Zeměpisná délka: <b>{data?.wgs84Coordinates?.lon}</b>
          </p>
        </>
      </Typography>
      <br />

      <ButtonGroup variant="contained">
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            console.log('FacilityMarkerBody, setActive', data.id);
            setActive(data.id);
          }}
        >
          Spravovat
        </Button>
        {Actions && <Actions data={data} />}
      </ButtonGroup>
      <br />
      <br />
    </>
  );
}

export const IrzFacilitiesMarkerPopupBody = ({
  data,
  setActive,
  Actions,
}: {
  data: IrzFacility;
  marker: CrzpMarker;
  setActive: (id: string) => void;
  Actions?: ({ data }: { data?: IrzFacility | undefined }) => JSX.Element;
}): React.ReactElement => {
  return (
    <div
      style={{
        backgroundColor: 'white',
        color: 'black',
      }}
    >
      <FacilityMarkerTitle name={data?.name ?? ''} />
      <FacilityMarkerBody
        data={data!}
        setActive={() => {
          setActive(data.id);
        }}
        Actions={Actions}
      />
    </div>
  );
};

const inactiveIcon = createMarkerIcon(MarkerType.FILLED_GREY, 'small');
const activeIcon = createMarkerIcon(MarkerType.FILLED_BLUE, 'small');
const selectedIconActive = createMarkerIcon(MarkerType.BIG_RED, 'medium');
const selectedIconInactive = createMarkerIcon(MarkerType.BIG_GREY, 'medium');

export function IrzFacilitiesMap() {
  const { setView } = useContext(MapEvidenceContext);
  const { setActive, onPersisted, source } = useContext(DetailContext);

  const activateFacility = (id: string) => {
    setActive(id);
    onPersisted(id);
    setView(MapEvidenceView.DETAIL);
  };

  const config = useMarkerPopupFactory<IrzFacility>({
    fetchData: (marker) => fetchIrzFacility(marker.id ?? '')?.json(),
    PopupBodyComponent: (props) =>
      IrzFacilitiesMarkerPopupBody({ ...props, setActive: activateFacility }),
  });

  const customCreateLeafletMarker = useCallback(
    (props: MarkerProps) => {
      let customIcon: any = {};
      const markerLeafletOptions = {} as L.MarkerOptions;
      if (source.data?.id === props.id) {
        customIcon = props.active ? selectedIconActive : selectedIconInactive;
        markerLeafletOptions.title = props.active ? 'Aktivní' : 'Neaktivní';
      } else if (props.active) {
        customIcon = activeIcon;
        markerLeafletOptions.title = 'Aktivní';
      } else {
        customIcon = inactiveIcon;
        markerLeafletOptions.title = 'Neaktivní';
      }

      return createLeafletMarker({
        ...props,
        customIcon,
        markerLeafletOptions,
      });
    },
    [source.data?.id]
  );

  return (
    <MapContainer showSuggest>
      <RemoteMarkers
        api={EvidenceAPI.IRZ_FACILITIES}
        markerPopupConfig={config}
        createLeafletMarker={customCreateLeafletMarker}
      />
    </MapContainer>
  );
}
